// What the dashboard computes from scans to draw them: the tone of a page (its worst failure), the
// order of pages and the phase of a scan in progress. The findings of a scan (problems, summaries,
// filters) are in results-logic.ts. Pure functions, tested in scan-logic.test.ts.
import type { IssueCounts, Scan, Severity } from './api';
import { SEVERITIES } from './api';

export type Tone = Severity | 'review' | 'ok' | 'none';

/** The colour of a page or a scan: its worst failure; "review" when it only has items to review. */
export function worstTone(counts: IssueCounts | null | undefined): Tone {
  if (!counts) return 'none';
  const found = SEVERITIES.find((severity) => counts.accessibility.violations[severity] > 0);
  if (found) return found;
  return counts.accessibility.review > 0 ? 'review' : 'ok';
}

export const failuresOf = (counts: IssueCounts | null | undefined) =>
  counts ? SEVERITIES.reduce((sum, severity) => sum + counts.accessibility.violations[severity], 0) : 0;

/** Pages ordered for the eye: worst first, then by failures, then by address. */
export function byWorst(
  a: { counts: IssueCounts | null; url: string },
  b: { counts: IssueCounts | null; url: string },
) {
  const rank = ['critica', 'grave', 'moderada', 'leve', 'review', 'ok', 'none'];
  return (
    rank.indexOf(worstTone(a.counts)) - rank.indexOf(worstTone(b.counts)) ||
    failuresOf(b.counts) - failuresOf(a.counts) ||
    a.url.localeCompare(b.url)
  );
}

export type LivePhase = 'queued' | 'preparing' | 'analysing' | 'finishing';
export const LIVE_PHASES: LivePhase[] = ['queued', 'preparing', 'analysing', 'finishing'];

/**
 * The phase of a scan that has not ended, from what the engine has reported: waiting in the queue,
 * preparing (nothing analysed or being analysed yet), analysing pages, or finishing (everything it
 * knows of is analysed and the estimate is being worked out). Never a percentage; a rough ETA from measured
 * pace lives in the live view only.
 */
export function livePhase(input: {
  status: 'queued' | 'running';
  done: number;
  total: number;
  current: number;
}): LivePhase {
  if (input.status === 'queued') return 'queued';
  if (input.done === 0 && input.current === 0) return 'preparing';
  if (input.total > 0 && input.done >= input.total && input.current === 0) return 'finishing';
  return 'analysing';
}

/** What a trend needs before it says anything: scans that can be compared, and a score that moved. */
export const TREND_MINIMUM = 3;

export interface TrendPoint {
  id: string;
  date: string;
  score: number;
}

/**
 * The scans whose estimates can be put on one line, or null when the line would say nothing. Comparable
 * means the same kind of scan (a quick scan of three pages and a full one of fifty are not the same
 * measure), completed (a partial one is missing pages) and not a check of some corrections or a custom list
 * of pages (they look at other things). It needs at least `TREND_MINIMUM` of them, and the estimate must
 * have changed at least once: a flat line of equal values is not an evolution. Oldest first.
 */
export function comparableTrend(
  scans: Pick<Scan, 'id' | 'type' | 'outcome' | 'createdAt' | 'scores' | 'customPages'>[],
): TrendPoint[] | null {
  const completed = scans
    .filter(
      (scan) =>
        scan.outcome === 'complete' &&
        !scan.customPages &&
        ['pagina', 'rapido', 'completo'].includes(scan.type),
    )
    .map((scan) => ({ ...scan, score: scan.scores?.accesibilidad.score ?? null }))
    .filter((scan): scan is typeof scan & { score: number } => scan.score !== null)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const kind = completed.at(-1)?.type;
  const points = completed
    .filter((scan) => scan.type === kind)
    .map((scan) => ({ id: scan.id, date: scan.createdAt, score: scan.score }));
  if (points.length < TREND_MINIMUM) return null;
  return new Set(points.map((point) => point.score)).size > 1 ? points : null;
}
